/**
 * Composite Patterns — Adaptive Intelligence Layer (I-A-11)
 *
 * Rule-based multi-signal pattern detection over existing data.
 * All patterns are deterministic, explainable, and bounded.
 *
 * Rules:
 *  - Pattern adjustments capped to ±5 points
 *  - Combined (learning + pattern) capped to ±20 total
 *  - Every pattern is explainable with human-readable context
 *  - No ML, no black-box, no cross-org blending
 */

import {
  MAX_COMBINED_ADJUSTMENT,
  MIN_COMBINED_ADJUSTMENT,
  type LearningPattern,
} from './learningWeights';

// ── Types ──────────────────────────────────────────────

export interface RecurrenceEntry {
  signal_type: string;
  shop_id: string;
  occurrence_count: number;
  last_resolved_at: string | null;
  avg_days_between: number | null;
}

export interface CompositePattern {
  pattern_type: CompositePatternType;
  confidence: 'low' | 'medium' | 'high';
  adjustment: number;          // bounded ±5
  explanation: string;          // human-readable summary
  context_lines: string[];     // detail lines for UI
  affected_shop_ids: string[];
}

export type CompositePatternType =
  | 'OPERATIONAL_BOTTLENECK'
  | 'DEMAND_WEAKNESS'
  | 'SYSTEMIC_IMBALANCE'
  | 'RECURRING_CONDITION';

export interface RecommendationWithPatterns {
  base_priority: number;
  learning_adjustment: number;
  pattern_adjustment: number;
  final_priority: number;        // base + learning + pattern, capped ±20
  learning_reasons: string[];
  pattern_reasons: string[];
  confidence: 'low' | 'medium' | 'high' | null;
}

// ── Constants ──────────────────────────────────────────

const MAX_PATTERN_ADJUSTMENT = 5;
const MIN_PATTERN_ADJUSTMENT = -5;
// IV-D-22 R74 FIX-2: MAX/MIN_COMBINED_ADJUSTMENT are the shared cap for
// learning + pattern + outcome adjustments. Previously duplicated here,
// in outcomeAdjustments.ts, and in learningWeights.ts — if any one of the
// three drifted (e.g. bumped to ±30 in learningWeights), the other two
// silently kept the old cap and priority scores would diverge between
// recommendations paths that route through different composers. Imported
// from learningWeights.ts (the canonical definition) above.

// ── Recommendation shape (minimal required fields) ────

interface RecommendationLike {
  type: string;
  shop_id?: string;
  shop_name?: string;
  priority_score: number;
  severity: string;
}

// ── Pattern Detection ──────────────────────────────────

/**
 * Detect composite patterns from recommendations, recurrence data,
 * and learning patterns. Pure, deterministic, rule-based.
 */
export function detectCompositePatterns(
  recommendations: RecommendationLike[],
  recurrenceData: RecurrenceEntry[],
  learningPatterns: LearningPattern[],
): CompositePattern[] {
  const patterns: CompositePattern[] = [];

  // Build lookup maps
  const shopTypes = new Map<string, Set<string>>();
  recommendations.forEach((r) => {
    if (r.shop_id) {
      if (!shopTypes.has(r.shop_id)) shopTypes.set(r.shop_id, new Set());
      shopTypes.get(r.shop_id)!.add(r.type);
    }
  });

  // ── OPERATIONAL_BOTTLENECK ──
  // Shop has OVERLOAD_ATTENTION + WAITER_PRESSURE + (optionally COMPLETION_WEAKNESS)
  shopTypes.forEach((types, shopId) => {
    const hasOverload = types.has('OVERLOAD_ATTENTION');
    const hasWaiter = types.has('WAITER_PRESSURE');
    const hasCompletion = types.has('COMPLETION_WEAKNESS');

    if (hasOverload && hasWaiter) {
      const convergence = (hasOverload ? 1 : 0) + (hasWaiter ? 1 : 0) + (hasCompletion ? 1 : 0);
      const confidence = convergence >= 3 ? 'high' : 'medium';
      const shopName = recommendations.find((r) => r.shop_id === shopId)?.shop_name ?? shopId;

      patterns.push({
        pattern_type: 'OPERATIONAL_BOTTLENECK',
        confidence,
        adjustment: 5,
        explanation: `${shopName} shows converging overload, waiter pressure${hasCompletion ? ', and declining completion' : ''}`,
        context_lines: [
          `${convergence} signal types converge at this location`,
          hasCompletion
            ? 'High load, elevated waiters, and declining output suggest a structural bottleneck'
            : 'High load combined with elevated waiter mix indicates capacity strain',
        ],
        affected_shop_ids: [shopId],
      });
    }
  });

  // ── DEMAND_WEAKNESS ──
  // Shop has UNDERUTILIZATION with low activity
  shopTypes.forEach((types, shopId) => {
    if (types.has('UNDERUTILIZATION') && types.size === 1) {
      const shopName = recommendations.find((r) => r.shop_id === shopId)?.shop_name ?? shopId;
      patterns.push({
        pattern_type: 'DEMAND_WEAKNESS',
        confidence: 'low',
        adjustment: -5,
        explanation: `${shopName} shows isolated underutilization with no competing signals`,
        context_lines: [
          'Low load without offsetting pressure signals may indicate a demand issue',
        ],
        affected_shop_ids: [shopId],
      });
    }
  });

  // ── SYSTEMIC_IMBALANCE ──
  // Multiple shops in overload state
  const overloadShops = recommendations
    .filter((r) => r.type === 'OVERLOAD_ATTENTION' && r.shop_id)
    .map((r) => r.shop_id!);
  const uniqueOverload = [...new Set(overloadShops)];

  if (uniqueOverload.length >= 2) {
    const recurrenceForOverload = recurrenceData.filter(
      (rd) => rd.signal_type === 'OVERLOAD_ATTENTION' && uniqueOverload.includes(rd.shop_id),
    );
    const totalRecurrence = recurrenceForOverload.reduce((sum, r) => sum + r.occurrence_count, 0);
    const confidence: CompositePattern['confidence'] =
      totalRecurrence >= 8 ? 'high' : totalRecurrence >= 4 ? 'medium' : 'low';

    patterns.push({
      pattern_type: 'SYSTEMIC_IMBALANCE',
      confidence,
      adjustment: 5,
      explanation: `${uniqueOverload.length} shops are simultaneously in overload state`,
      context_lines: [
        `${uniqueOverload.length} locations showing elevated OBLI at the same time`,
        totalRecurrence > 0
          ? `Combined ${totalRecurrence} overload occurrences in the last 30 days`
          : 'First simultaneous detection in this period',
      ],
      affected_shop_ids: uniqueOverload,
    });
  }

  // ── RECURRING_CONDITION ──
  // Same signal type retriggering at same shop (recurrence count >= 3)
  recurrenceData
    .filter((rd) => rd.occurrence_count >= 3)
    .forEach((rd) => {
      const confidence: CompositePattern['confidence'] =
        rd.occurrence_count >= 5 ? 'high' : rd.occurrence_count >= 3 ? 'medium' : 'low';

      const shopName = recommendations.find((r) => r.shop_id === rd.shop_id)?.shop_name ?? rd.shop_id;
      const avgDaysStr = rd.avg_days_between != null
        ? ` (avg ${rd.avg_days_between} days between occurrences)`
        : '';

      patterns.push({
        pattern_type: 'RECURRING_CONDITION',
        confidence,
        adjustment: 5,
        explanation: `${shopName} has a recurring ${formatSignalType(rd.signal_type)} condition`,
        context_lines: [
          `This condition has occurred ${rd.occurrence_count} times in the last 30 days${avgDaysStr}`,
          rd.last_resolved_at
            ? `Last resolved: ${new Date(rd.last_resolved_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
            : 'Currently unresolved',
        ],
        affected_shop_ids: [rd.shop_id],
      });
    });

  return patterns;
}

// ── Pattern Adjustment Application ─────────────────────

/**
 * Apply pattern adjustments to recommendations that already have learning weights.
 * Combined (learning + pattern) adjustment is hard-capped at ±20.
 */
export function applyPatternAdjustments<
  T extends {
    type: string;
    shop_id?: string;
    base_priority: number;
    learning_adjustment: number;
    final_priority: number;
    learning_reasons: string[];
  },
>(
  recs: T[],
  compositePatterns: CompositePattern[],
): (T & RecommendationWithPatterns)[] {
  return recs.map((rec) => {
    // Find matching patterns for this rec's shop or type
    const matchingPatterns = compositePatterns.filter((p) =>
      (rec.shop_id && p.affected_shop_ids.includes(rec.shop_id)) ||
      p.pattern_type === typeToPattern(rec.type),
    );

    if (matchingPatterns.length === 0) {
      return {
        ...rec,
        pattern_adjustment: 0,
        pattern_reasons: [],
        confidence: null as 'low' | 'medium' | 'high' | null,
        final_priority: rec.final_priority, // keep learning-adjusted value
      };
    }

    // Sum pattern adjustments, capped at ±5
    let patternAdj = matchingPatterns.reduce((sum, p) => sum + p.adjustment, 0);
    patternAdj = Math.max(MIN_PATTERN_ADJUSTMENT, Math.min(MAX_PATTERN_ADJUSTMENT, patternAdj));

    // Combined cap: learning + pattern ≤ ±20
    let combinedAdj = rec.learning_adjustment + patternAdj;
    combinedAdj = Math.max(MIN_COMBINED_ADJUSTMENT, Math.min(MAX_COMBINED_ADJUSTMENT, combinedAdj));

    // Derive effective pattern adjustment after combined cap
    const effectivePatternAdj = combinedAdj - rec.learning_adjustment;

    // Highest confidence among matching patterns
    const confidence = highestConfidence(matchingPatterns.map((p) => p.confidence));

    // Collect reasons and context lines
    const patternReasons: string[] = [];
    matchingPatterns.forEach((p) => {
      patternReasons.push(p.explanation);
      patternReasons.push(...p.context_lines);
    });

    return {
      ...rec,
      pattern_adjustment: effectivePatternAdj,
      pattern_reasons: patternReasons,
      confidence,
      final_priority: rec.base_priority + combinedAdj,
    };
  });
}

// ── Helpers ────────────────────────────────────────────

function typeToPattern(recType: string): CompositePatternType | null {
  switch (recType) {
    case 'OVERLOAD_ATTENTION': return 'OPERATIONAL_BOTTLENECK';
    case 'UNDERUTILIZATION': return 'DEMAND_WEAKNESS';
    case 'CAPACITY_IMBALANCE': return 'SYSTEMIC_IMBALANCE';
    default: return null;
  }
}

function highestConfidence(levels: ('low' | 'medium' | 'high')[]): 'low' | 'medium' | 'high' {
  if (levels.includes('high')) return 'high';
  if (levels.includes('medium')) return 'medium';
  return 'low';
}

function formatSignalType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get pattern confidence badge label for UI display.
 */
export function getPatternBadgeLabel(pattern: CompositePattern): string {
  switch (pattern.pattern_type) {
    case 'OPERATIONAL_BOTTLENECK': return 'Bottleneck';
    case 'DEMAND_WEAKNESS': return 'Demand Issue';
    case 'SYSTEMIC_IMBALANCE': return 'Systemic';
    case 'RECURRING_CONDITION': return 'Recurring Issue';
  }
}

export function getConfidenceBadgeClass(confidence: 'low' | 'medium' | 'high'): string {
  switch (confidence) {
    case 'high': return 'bg-destructive/20 text-destructive border-destructive/30';
    case 'medium': return 'bg-warning/20 text-warning border-warning/30';
    case 'low': return 'bg-muted text-muted-foreground border-border';
  }
}
